/** @jsxImportSource ../../i18n */
import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Link2, WifiOff } from 'lucide-react';
import { cx } from '../../theme/cx';
import { Badge, Button, Card, EmptyState, Spinner } from '../ui';

export const useBroker = () => {
  const { saxoConnected, saxoStatusChecked, backendAvailable, saxoAuthUrl } = useSelector((state) => state.portfolio);
  let status = 'connected';
  if (!saxoStatusChecked) status = 'checking';
  else if (!backendAvailable) status = 'offline';
  else if (!saxoConnected) status = 'disconnected';
  return {
    name: 'Saxo Bank',
    shortName: 'Saxo',
    status,
    authUrl: saxoAuthUrl,
  };
};

export const BrokerStatus = ({ className }) => {
  const { shortName, status, authUrl } = useBroker();
  if (status === 'checking') return <Badge tone="neutral" dot="pulse" className={className}>Connexion…</Badge>;
  if (status === 'offline') return <Badge tone="loss" dot className={className}>Serveur injoignable</Badge>;
  if (status === 'connected') return <Badge tone="gain" dot className={className}>{shortName} connecté</Badge>;
  const linkClass = cx(
    'h-6 items-center gap-1 whitespace-nowrap rounded-full border border-warn-500/25 bg-warn-900/70 px-2 text-xs font-medium leading-none text-warn-300 transition-colors hover:border-warn-500/50',
    className || 'inline-flex',
  );
  return authUrl
    ? <a href={authUrl} className={linkClass}><Link2 size={12} aria-hidden="true" />Connecter {shortName}</a>
    : <Link to="/settings" className={linkClass}><Link2 size={12} aria-hidden="true" />Connecter {shortName}</Link>;
};

export const BrokerGate = ({ children }) => {
  const { name, status, authUrl } = useBroker();
  if (status === 'checking') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size={22} label="Connexion au serveur…" />
      </div>
    );
  }
  if (status === 'offline') {
    return (
      <Card>
        <EmptyState
          icon={WifiOff}
          tone="loss"
          title="Serveur injoignable"
          description="Le serveur ne répond pas pour le moment. Nouvelle tentative automatique toutes les 10 secondes."
        />
      </Card>
    );
  }
  if (status === 'disconnected') {
    return (
      <Card tone="gold" padding="lg">
        <EmptyState
          icon={Link2}
          title={`Connectez votre compte ${name}`}
          description={`Pour afficher votre portefeuille et suivre vos performances, connectez votre compte ${name}.`}
          action={authUrl
            ? <Button as="a" href={authUrl} leadingIcon={Link2}>Connecter {name}</Button>
            : <Button as={Link} to="/settings" variant="secondary">Ouvrir les réglages</Button>}
        />
      </Card>
    );
  }
  return children;
};
